import { FC, ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { Fade } from "@mui/material";
import {
  ModalButton,
  CloseModalButton,
  BackModalButton,
} from "components/Buttons";
import { TextInput } from "components/Inputs";
import { PasswordHintRow } from "components/Rows";
import { AppText } from "components";
import { ModalHeading, Form, InputContainer } from "../styled";
import { ReactComponent as LockIcon } from "assets/icons/lock-unlocked.svg";

interface MainChangePassStepProps {
  isSubmitting: boolean;
  values: { [field: string]: any };
  onClose: () => void;
  errors: { [field: string]: string };
  handleBlur: (e: any) => void;
  handleChange: (e: ChangeEvent<any>) => void;
  handleSubmit: () => void;
  touched: { [field: string]: boolean };
  isMFAEnabled: boolean;
}

const MainChangePassStep: FC<MainChangePassStepProps> = ({
  onClose,
  isSubmitting,
  handleSubmit,
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  isMFAEnabled,
}) => {
  const { t } = useTranslation();

  return (
    <Fade in timeout={500}>
      <Form onSubmit={handleSubmit}>
        <ModalHeading $centered $capitalized>
          {t("CHANGE_PASSWORD")}
        </ModalHeading>
        <CloseModalButton onClose={onClose} />
        <BackModalButton onBack={onClose} />

        <InputContainer style={{ marginTop: "1rem" }}>
          <AppText>{t("CURRENT_PASSWORD")}</AppText>
          <TextInput
            type="password"
            name="oldPassword"
            placeholder={t("ENTER_CURRENT_PASSWORD")}
            value={values.oldPassword}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.oldPassword && Boolean(errors.oldPassword)}
            helperText={
              touched.oldPassword && errors.oldPassword && t(errors.oldPassword)
            }
          />
        </InputContainer>

        <InputContainer>
          <AppText>{t("NEW_PASSWORD")}</AppText>
          <TextInput
            type="password"
            name="newPassword"
            placeholder={t("ENTER_NEW_PASSWORD")}
            value={values.newPassword}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.newPassword && Boolean(errors.newPassword)}
            helperText={
              touched.newPassword && errors.newPassword && t(errors.newPassword)
            }
          />
        </InputContainer>

        <PasswordHintRow newPassword={values.newPassword} />

        <InputContainer>
          <AppText>{t("CONFIRM_PASSWORD")}</AppText>
          <TextInput
            type="password"
            name="confirmPass"
            placeholder={t("CONFIRM_PASSWORD")}
            value={values.confirmPass}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.confirmPass && Boolean(errors.confirmPass)}
            helperText={
              touched.confirmPass && errors.confirmPass && t(errors.confirmPass)
            }
          />
        </InputContainer>

        {isMFAEnabled && (
          <InputContainer>
            <AppText>{t("2FA Code")}</AppText>
            <TextInput
              placeholder={t("CODE")}
              name="oneTimePassword"
              value={values.oneTimePassword}
              onChange={handleChange}
              leftIcon={<LockIcon />}
              autoComplete="off"
            />
          </InputContainer>
        )}

        <ModalButton
          variant="contained"
          fullWidth
          type="submit"
          style={{ marginTop: "2.8rem" }}
          disabled={isSubmitting || (isMFAEnabled && !values.oneTimePassword)}
        >
          {t("CHANGE")}
        </ModalButton>
      </Form>
    </Fade>
  );
};

export default MainChangePassStep;
